import React, { useState } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import DoneIcon from '@mui/icons-material/Done';

// Styled components for modern quiz design
const QuestionCard = styled(Card)(({ theme }) => ({
  background: 'transparent',
  boxShadow: 'none',
  borderRadius: '16px',
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: '12px',
  borderRadius: '6px',
  backgroundColor: 'rgba(102, 126, 234, 0.1)',
  '& .MuiLinearProgress-bar': {
    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '6px',
  },
}));

const QuestionHeader = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(4),
}));

const QuestionText = styled(Typography)(({ theme }) => ({
  fontSize: '1.2rem',
  lineHeight: 1.6,
  color: '#e2e8f0',
  marginBottom: theme.spacing(3),
  padding: theme.spacing(3),
  background: '#1a1a2e',
  borderRadius: '12px',
  border: '1px solid rgba(102, 126, 234, 0.1)',
}));

const OptionsPaper = styled(Paper)(({ theme }) => ({
  background: 'transparent',
  padding: theme.spacing(2),
  borderRadius: '12px',
  marginBottom: theme.spacing(3),
  boxShadow: 'none',
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme, isSelected, isCorrect, isIncorrect }) => ({
  width: '100%',
  margin: theme.spacing(1, 0),
  padding: theme.spacing(2),
  borderRadius: '12px',
  border: '2px solid',
  borderColor: isSelected 
    ? (isCorrect ? '#48bb78' : isIncorrect ? '#f56565' : '#667eea')
    : 'rgba(102, 126, 234, 0.1)',
  background: isSelected
    ? (isCorrect ? 'rgba(72, 187, 120, 0.1)' : isIncorrect ? 'rgba(245, 101, 101, 0.1)' : 'rgba(102, 126, 234, 0.1)')
    : '#1a1a2e',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: isSelected 
      ? (isCorrect ? '#48bb78' : isIncorrect ? '#f56565' : '#667eea')
      : 'rgba(102, 126, 234, 0.2)',
    background: isSelected
      ? (isCorrect ? 'rgba(72, 187, 120, 0.15)' : isIncorrect ? 'rgba(245, 101, 101, 0.15)' : 'rgba(102, 126, 234, 0.15)')
      : 'rgba(102, 126, 234, 0.05)',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)',
  },
  '& .MuiFormControlLabel-label': {
    fontSize: '1rem',
    fontWeight: 500,
    color: isSelected 
      ? (isCorrect ? '#48bb78' : isIncorrect ? '#f56565' : '#e2e8f0')
      : '#cbd5e1',
    width: '100%',
  },
  '& .MuiRadio-root': {
    color: isSelected 
      ? (isCorrect ? '#48bb78' : isIncorrect ? '#f56565' : '#667eea')
      : '#cbd5e1',
  },
}));

const ActionButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: theme.spacing(4),
}));

const NextButton = styled(Button)(({ theme, isEnabled }) => ({
  borderRadius: '12px',
  padding: theme.spacing(1.5, 4),
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '1.1rem',
  background: isEnabled 
    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    : 'rgba(102, 126, 234, 0.1)',
  color: isEnabled ? 'white' : '#cbd5e1',
  boxShadow: isEnabled ? '0 4px 16px rgba(102, 126, 234, 0.3)' : 'none',
  '&:hover': {
    background: isEnabled 
      ? 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)'
      : 'rgba(102, 126, 234, 0.1)',
    transform: isEnabled ? 'translateY(-2px)' : 'none',
    boxShadow: isEnabled ? '0 8px 25px rgba(102, 126, 234, 0.4)' : 'none',
  },
  '&:disabled': {
    background: 'rgba(102, 126, 234, 0.1)',
    color: '#cbd5e1',
  },
}));

const FeedbackAlert = styled(Alert)(({ theme, severity }) => ({
  borderRadius: '12px',
  margin: theme.spacing(3, 0),
  fontSize: '1rem',
  border: '1px solid',
  borderColor: severity === 'success' ? '#48bb78' : '#f56565',
  '& .MuiAlert-icon': {
    fontSize: '1.5rem',
  },
}));

export default function Quiz({ questions, onFinish }) {
  const [step, setStep] = useState(0);
  const [selected, setSelected] = useState(null);
  const [score, setScore] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);

  const q = questions[step];
  const isCorrect = selected === q.answer;
  const isLastQuestion = step + 1 === questions.length;

  const handleOptionSelect = (value) => {
    setSelected(Number(value));
    setShowFeedback(true);
  };

  const handleNext = () => {
    if (isCorrect) setScore((s) => s + 1);
    const nextStep = step + 1;
    setSelected(null);
    setShowFeedback(false);
    
    if (nextStep < questions.length) {
      setStep(nextStep);
    } else {
      const finalScore = Math.round(((isCorrect ? score + 1 : score) / questions.length) * 100);
      onFinish(finalScore);
    }
  };

  const progressValue = ((step + 1) / questions.length) * 100;

  return (
    <QuestionCard>
      <CardContent sx={{ p: 0 }}>
        {/* Progress Bar */}
        <ProgressContainer>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#cbd5e1' }}>
              Progresso
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#667eea' }}>
              {step + 1} de {questions.length}
            </Typography>
          </Box>
          <StyledLinearProgress variant="determinate" value={progressValue} />
        </ProgressContainer>

        {/* Question Header */}
        <QuestionHeader>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#f1f5f9', mb: 1 }}>
            Questão {step + 1}
          </Typography>
          <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
            Selecione a alternativa correta
          </Typography>
        </QuestionHeader>

        {/* Question Text */}
        <QuestionText variant="body1">
          {q.stem}
        </QuestionText>

        {/* Options */}
        <OptionsPaper>
          <RadioGroup
            value={selected !== null ? selected : ''}
            onChange={(_, v) => handleOptionSelect(v)}
          >
            {q.options.map((opt, i) => (
              <StyledFormControlLabel
                key={i}
                value={i}
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <span>{opt}</span>
                    {showFeedback && selected === i && (
                      isCorrect ? (
                        <CheckCircleIcon sx={{ color: '#48bb78', ml: 1 }} />
                      ) : (
                        <CancelIcon sx={{ color: '#f56565', ml: 1 }} />
                      )
                    )}
                    {showFeedback && !isCorrect && i === q.answer && (
                      <CheckCircleIcon sx={{ color: '#48bb78', ml: 1 }} />
                    )}
                  </Box>
                }
                isSelected={selected === i}
                isCorrect={showFeedback && i === q.answer}
                isIncorrect={showFeedback && selected === i && !isCorrect}
              />
            ))}
          </RadioGroup>
        </OptionsPaper>

        {/* Feedback */}
        {showFeedback && (
          <FeedbackAlert 
            severity={isCorrect ? 'success' : 'error'}
            icon={isCorrect ? <CheckCircleIcon /> : <CancelIcon />}
          >
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
              {isCorrect ? 'Correto!' : 'Incorreto.'}
            </Typography>
            <Typography variant="body2">
              {q.explanation}
            </Typography>
          </FeedbackAlert>
        )}

        {/* Action Button */}
        <ActionButtonContainer>
          <NextButton
            isEnabled={showFeedback}
            disabled={!showFeedback}
            onClick={handleNext}
            endIcon={isLastQuestion ? <DoneIcon /> : <NavigateNextIcon />}
          >
            {isLastQuestion ? 'Finalizar Quiz' : 'Próxima Questão'}
          </NextButton>
        </ActionButtonContainer>
      </CardContent>
    </QuestionCard>
  );
}
